/**
 * Fare-breakdown receipt helper — purely presentational component
 * used inside Passenger.jsx (iter-79 decomposition slice 2).
 *
 * Strict isolation contract (matches Slice 1 / Driver.jsx playbook):
 *   - Zero state, zero hooks, zero context, zero external helpers.
 *   - Props are the only inputs; output is JSX only.
 *   - Behaviour preserved exactly from the original inline definition.
 *
 * Props:
 *   b       — fare-breakdown object (see backend pricing service)
 *   compact — boolean; renders the smaller receipt variant used inside
 *             active/completed ride cards
 *
 * Used by Passenger.jsx in three places:
 *   1. live fare estimate panel (full size)
 *   2. ride receipt card (compact)
 *   3. ride receipt drawer (compact)
 */
import React from "react";

export const FareBreakdown = ({ b, compact }) => {
  const rows = [];
  if (!b) return null;
  if (b.base_fare > 0 && !b.is_fixed_route) rows.push(["Base fare", b.base_fare]);
  if (b.distance_charge > 0) rows.push(["Distance", b.distance_charge]);
  if (b.time_charge > 0) rows.push(["Time", b.time_charge]);
  if (b.surge_multiplier && b.surge_multiplier > 1.0) rows.push([`Surge ×${b.surge_multiplier}`, null]);
  if (b.meter_total && (b.distance_charge === 0 || b.surge_multiplier > 1.0)) {
    // fixed route or post-surge — already shown above as a sum
  }
  if (b.extras?.extra_stops_total > 0) rows.push([`Extra stops × ${b.extras.extra_stops_count}`, b.extras.extra_stops_total]);
  if (b.extras?.dog_total > 0) rows.push(["Dog fee", b.extras.dog_total]);
  if (b.extras?.terminal_total > 0) rows.push(["Terminal drop-off", b.extras.terminal_total]);
  if (b.wait_and_return) rows.push(["Wait & return (min £9)", null]);
  if (b.late_night_amount > 0) rows.push(["Late-night surcharge", b.late_night_amount]);

  return (
    <div className={`font-mono-tab ${compact ? "text-xs" : "text-sm"} space-y-1`} data-testid="fare-breakdown">
      {rows.map(([label, val]) => (
        <div key={label} className="flex justify-between text-muted-foreground">
          <span>{label}</span>
          <span>{val == null ? "—" : `£${val.toFixed(2)}`}</span>
        </div>
      ))}
      <div className="border-t border-border pt-1 mt-1 space-y-0.5">
        <div className="flex justify-between text-muted-foreground">
          <span>Net (ex-VAT)</span>
          <span>£{b.subtotal_pre_vat_net.toFixed(2)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>VAT @ {Math.round((b.vat_rate || 0) * 100)}%</span>
          <span>£{b.vat_amount.toFixed(2)}</span>
        </div>
        <div className="flex justify-between font-bold text-foreground">
          <span>Total</span>
          <span>£{b.gross.toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
};

export default FareBreakdown;
